import { useContext, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import mapSliceToComponent from '~/imports/product_page'
import { STORYBLOK_TYPES } from '~/lib/storyblok-types'

import { ProductData } from '@unlikelystudio/react-abstract-components'

import SliceManager from '~/components/Abstracts/SliceManager'
import { PageViewAnalytics } from '~/components/PageViewAnalytics'
import ProductHeader from '~/components/UI/ProductHeader'
import RecentlyViewedProductsGrid from '~/components/UI/RecentlyViewedProductsGrid'
import RecentlyViewedWrapper from '~/components/UI/RecentlyViewedWrapper'
import RecommendedProductsGrid from '~/components/UI/RecommendedProductsGrid'

import ShopifyProductProvider from '~/providers/ShopifyProductProvider'

import { addProductViewed } from '~/hooks/useProductsRecentlyViewed'
import { ProductsViewedContext } from '~/hooks/useProductsRecentlyViewed/context'

import { getStaticPathsFunction } from '~/data/page-data'
import getProductPageStaticPropsFunction from '~/data/product-page-data'
import { ProductPageData } from '~/data/product-page-data/serializer'

import css from './styles.module.scss'

export default function Product({
  slices,
  productHeader,
  currentProduct,
  recommendationData,
}: ProductPageData) {
  const ProductViewedCtx = useContext(ProductsViewedContext)

  useEffect(() => {
    if (currentProduct?.uid && currentProduct?.locale) {
      addProductViewed(
        currentProduct?.uid,
        currentProduct?.locale,
        ProductViewedCtx,
      )
    }
  }, [currentProduct?.uid, currentProduct?.locale, ProductViewedCtx])

  const {
    ref: inViewBottomMarkerRef,
    inView: markerInViewBottom,
    entry,
  } = useInView({
    threshold: 0.2,
    initialInView: true,
  })

  // Making sure even if we pass the marker that the footer is still mark visibled
  const isVisible = markerInViewBottom || entry?.boundingClientRect?.y <= 0

  return (
    <>
      {productHeader?.jsonLD && <ProductData {...productHeader.jsonLD} />}
      {productHeader && (
        <ShopifyProductProvider
          gid={productHeader?.shopifyId}
          isAdjustable={productHeader?.isAdjusting}
          adjustableOffset={productHeader?.adjustableOffset}>
          <ProductHeader isFooterVisible={isVisible} {...productHeader} />
        </ShopifyProductProvider>
      )}
      {recommendationData &&
        (recommendationData?.categoryUUID ||
          recommendationData?.designerUUID ||
          recommendationData?.siblingsStories) && (
          <RecommendedProductsGrid recommendationData={recommendationData} />
        )}
      <RecentlyViewedWrapper Component={RecentlyViewedProductsGrid} />
      <SliceManager
        mapSliceTypeToComponent={mapSliceToComponent}
        slicesData={slices}
      />

      <div className={css.inViewMarker} ref={inViewBottomMarkerRef} />
      <PageViewAnalytics template="product" />
    </>
  )
}

export const getStaticProps = getProductPageStaticPropsFunction()

export const getStaticPaths = getStaticPathsFunction({
  type: STORYBLOK_TYPES.PRODUCT,
})
